import { type Channel, Socket as PhoenixSocket } from 'phoenix';
import { CONFIG } from '@frameio/config';
import { reportError } from '@frameio/telemetry';
import { datadogLogger } from '@frameio/telemetry/datadog/logger';
import { emitEvent } from '@frameio/telemetry/events';
import { auth } from '@frameio/utils';
import { hasAccessTokenExpired } from '@frameio/utils/auth/hasAccessTokenExpired';
import { isServer, isUXPEnvironment } from '@frameio/utils/config';
import { session } from '@frameio/utils/session';
import { handleUXPRefresh } from './handleUXPRefresh';

// Phoenix maintains channels at runtime, but @types/phoenix omits it.
// The @frameio/actions package need access to that array.
type PhoenixSocketWithChannels = PhoenixSocket & { channels: Channel[] };

const ATTEMPTS_ERROR_THRESHOLD = 10;
const BASE_DELAY_MS = 5000;

type SocketState = {
  socket: PhoenixSocket;
  isResetting: boolean;
  reconnectStartTime: number | null;
  reconnectTimer: NodeJS.Timeout | null;
  attemptCount: number;
};

// One socket per URL. Data regions each get their own connection, keyed by the
// regional socket URL; the default region lives under `CONFIG.socketUrl`.
const sockets = new Map<string, SocketState>();
const socketSubscribers = new Set<() => void>();
const sessionId = session.getSessionId();

// @see https://developer.mozilla.org/en-US/docs/Web/API/CloseEvent/code
function getCloseCodeDescription(code: number): string {
  const codes: Record<number, string> = {
    1000: 'Normal Closure',
    1001: 'Going Away (server shutdown)',
    1002: 'Protocol Error',
    1003: 'Unsupported Data',
    1005: 'No Status Received',
    1006: 'Abnormal Closure (no close frame)',
    1007: 'Invalid Frame Payload Data',
    1008: 'Policy Violation',
    1009: 'Message Too Big',
    1010: 'Mandatory Extension',
    1011: 'Internal Server Error',
    1015: 'TLS Handshake',
  };
  return codes[code] || 'Unknown';
}

function logSocketConnectionError(error: Error) {
  try {
    // We suspect that connection attempts are related to having too many cookies
    const cookies = document.cookie;
    const cookieSizeBytes = new Blob([cookies]).size;
    const errorInfo = {
      authExpired: hasAccessTokenExpired(auth.getAccessToken()),
      hasAccessToken: Boolean(auth.getAccessToken()),
      cookieSizeBytes,
      userId: auth.getUserId(),
    };
    reportError(error, {
      message: `Failed to connect to socket server after ${ATTEMPTS_ERROR_THRESHOLD} attempts`,
      ...errorInfo,
    });
    datadogLogger.error('socket_error', {
      error:
        error && typeof error === 'object' && 'message' in error
          ? error.message
          : (error as Error).toString(),
      errorInfo,
    });
  } catch (err) {
    reportError(err as Error, {
      message: 'Failed to log socket connection error',
    });
  }
}

export function connectSocket({
  socketUrl = CONFIG.socketUrl,
  onReset,
}: {
  socketUrl?: string;
  onReset: () => void;
}): PhoenixSocket {
  const existing = sockets.get(socketUrl);
  if (existing) {
    existing.socket.disconnect(() => existing.socket.connect());
    return existing.socket;
  }

  const accessToken = auth.getAccessToken();

  if (!accessToken) {
    throw new Error('Cannot connect socket without an access token');
  }

  if (isServer) {
    throw new Error('Cannot connect socket on server');
  }

  const state: SocketState = {
    socket: undefined as unknown as PhoenixSocket,
    isResetting: false,
    reconnectStartTime: null,
    reconnectTimer: null,
    attemptCount: 0,
  };

  const socket = new PhoenixSocket(socketUrl, {
    reconnectAfterMs: (tries: number) => {
      state.attemptCount = tries;

      // Track reconnection start time on first attempt
      if (tries === 1) {
        state.reconnectStartTime = Date.now();
      }

      const delayMs = Math.floor(Math.random() * BASE_DELAY_MS * 2 ** tries);

      if (tries === ATTEMPTS_ERROR_THRESHOLD) {
        logSocketConnectionError(
          new Error(
            `Failed to connect to socket after ${ATTEMPTS_ERROR_THRESHOLD} attempts`
          )
        );
      }

      // Track reconnection attempt
      emitEvent('socket_reconnect_attempt', {
        attempt: tries,
        isResetting: state.isResetting,
        delayMs,
      });

      return delayMs;
    },
    params: () => {
      if (isUXPEnvironment()) {
        handleUXPRefresh();
      } else {
        auth.getAccessTokenWithRefreshIfExpired();
      }
      return {
        Authorization: `Bearer ${auth.getAccessToken()}`,
        session_id: sessionId,
        apollo_client_name: CONFIG.thirdParty.apolloClient?.name,
        apollo_client_version: CONFIG.clientIdentifierVersion,
      };
    },
  });

  state.socket = socket;
  sockets.set(socketUrl, state);

  socket.onOpen(() => {
    if (state.reconnectTimer) {
      clearTimeout(state.reconnectTimer);
      state.reconnectTimer = null;
    }

    if (state.isResetting && state.reconnectStartTime) {
      const duration = Date.now() - state.reconnectStartTime;

      // Track successful reconnection after reset
      emitEvent('socket_reconnect_success', {
        attempts: state.attemptCount,
        durationMs: duration,
        wasResetting: true,
      });

      state.reconnectStartTime = null;
      state.attemptCount = 0;
      state.isResetting = false;
      onReset();
    } else if (state.reconnectStartTime) {
      const duration = Date.now() - state.reconnectStartTime;

      // Track successful reconnection (non-reset)
      emitEvent('socket_reconnect_success', {
        attempts: state.attemptCount,
        durationMs: duration,
        wasResetting: false,
      });

      state.reconnectStartTime = null;
      state.attemptCount = 0;
    }
  });
  // NOTE socket close fires on failure to connect
  socket.onClose((event: CloseEvent) => {
    const closeCode = event?.code;
    // When not clean or not 1000 phoenix will retry on its own
    // https://github.com/phoenixframework/phoenix/blob/v1.6.15/assets/js/phoenix/socket.js#L409-L411
    const willReset = closeCode === 1000;

    // Track socket close event with detailed information
    emitEvent('socket_close', {
      code: closeCode,
      codeDescription: getCloseCodeDescription(closeCode),
      reason: event?.reason || 'No reason provided',
      wasClean: event?.wasClean,
      willReset,
    });

    // Log detailed close information for debugging pod lifecycle
    datadogLogger.info('socket_close_detailed', {
      code: closeCode,
      codeDescription: getCloseCodeDescription(closeCode),
      reason: event?.reason,
      wasClean: event?.wasClean,
      timestamp: new Date().toISOString(),
      willReset,
    });

    if (willReset && !state.isResetting) {
      // server disconnect (possibly deploy)
      state.isResetting = true;
      const resetDelay = Math.floor(Math.random() * BASE_DELAY_MS);
      if (state.reconnectTimer) {
        clearTimeout(state.reconnectTimer);
      }
      state.reconnectTimer = setTimeout(() => {
        socket.disconnect(() => socket.connect());
      }, resetDelay);
    }
  });

  socketSubscribers.forEach((notify) => notify());
  return socket;
}

export function subscribeSocket(onChange: () => void): () => void {
  socketSubscribers.add(onChange);
  return () => {
    socketSubscribers.delete(onChange);
  };
}

export function getSocket(
  socketUrl: string = CONFIG.socketUrl
): PhoenixSocketWithChannels | null {
  return (sockets.get(socketUrl)?.socket as PhoenixSocketWithChannels) ?? null;
}
